/** Configuration — Retention — confirmed ordinary-account policy; other scopes NOT DEFINED */

export const RETENTION_ROUTE = '/admin/configuration/retention';

const DAYS_CALCULATION =
  'Complete 24-hour periods from the recorded start date and time, using the defined date/time basis of the selected start.';
const MONTHS_CALCULATION =
  'Calendar months from the recorded start date and time. Keep the corresponding day and time where valid. If that day does not exist in the resulting month, use the last valid day of that month. Use the defined date/time basis of the selected start.';
const YEARS_CALCULATION =
  'Calendar years from the recorded start date and time. Keep the corresponding day and time where valid. If that day does not exist in the resulting month, use the last valid day of that month. Use the defined date/time basis of the selected start.';

const ORDINARY_ACCOUNT_COVERAGE =
  'Ordinary account only. Does not automatically cover Posts, billing records, cases, Flags, evidence, Messages, Quotes, Change History or System Logs.';

const ACCOUNT_CLOSED_MEANING =
  'Retention starts on the successful Account Closed occurrence for the covered account and its recorded time. A closure request, inactivity, restriction or ban does not establish that occurrence. Repeated delivery of the same closure notification creates no new start.';

const ORDINARY_ACCOUNT_ANONYMISE_SCOPE =
  'Anonymise within the ordinary-account boundary: remove names, email, mobile, identifying profile, logo and location details, and sign-in credentials (for a Business, include Business Name and ABN in the ordinary Business profile). Retain permanent account ID, non-identifying history and necessary links. Preserve billing, cases, evidence, shared Messages and Quotes, and other separately retained records under their own requirements. Keep the account Closed. Creates no replacement account or new Account Status. Finally anonymised records cannot be restored.';

const DELETE_SCOPE =
  'Permanently delete only the covered record and its defined deletion scope. Linked records are not included unless they are the selected Record Type.';

/** Confirmed ordinary Customer and Business accounts only. Change History / System Logs / other scopes remain NOT DEFINED. */
export const RECORD_TYPE_OPTIONS = [
  {
    value: 'customer_account',
    label: 'Customer Account',
    coverage: ORDINARY_ACCOUNT_COVERAGE,
    reference: 'sample-rt-customer-account',
    available: true,
  },
  {
    value: 'business_account',
    label: 'Business Account',
    coverage: ORDINARY_ACCOUNT_COVERAGE,
    reference: 'sample-rt-business-account',
    available: true,
  },
];

export const STARTS_WHEN_OPTIONS = [
  {
    value: 'account_closed',
    label: 'Account Closed',
    recordTypes: ['customer_account', 'business_account'],
    occurrence: 'Successful Account Closed',
    belongsTo: 'The selected account record',
    dateTimeField: 'Account Closed recorded time',
    conditionInfo: 'Valid information for the account at the successful closure occurrence',
    dateTimeBasis: 'The recorded successful Account Closed date and time',
    meaning: ACCOUNT_CLOSED_MEANING,
    defined: true,
    available: true,
  },
];

/** Ordinary closed accounts use 120 Days. Other durations remain NOT DEFINED as selectable policy. */
export const RETENTION_PERIOD_OPTIONS = [
  {
    value: '120_days',
    label: '120 Days',
    amount: 120,
    unit: 'days',
    calculation: DAYS_CALCULATION,
    recordTypes: ['customer_account', 'business_account'],
    available: true,
  },
];

export const FINAL_ACTION_OPTIONS = [
  {
    value: 'delete_permanently',
    label: 'Delete Permanently',
    /** Change History / System Log deletion boundaries established; periods NOT DEFINED — not selectable yet. */
    recordTypes: [],
    scope: DELETE_SCOPE,
    defined: true,
    available: true,
  },
  {
    value: 'anonymise',
    label: 'Anonymise',
    recordTypes: ['customer_account', 'business_account'],
    scope: ORDINARY_ACCOUNT_ANONYMISE_SCOPE,
    defined: true,
    available: true,
  },
];

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals', multi: false },
  { value: 'does_not_equal', label: 'Does Not Equal', multi: false },
  { value: 'is_any_of', label: 'Is Any Of', multi: true },
  { value: 'is_not_any_of', label: 'Is Not Any Of', multi: true },
];

/** Optional Condition catalogue — NOT DEFINED. Keep empty until confirmed. */
export const CONDITION_FIELD_OPTIONS = [];

const LEGACY_RECORD_TYPE_MAP = {
  customer_record: 'customer_account',
  customer_closed_account_record: 'customer_account',
  business_record: 'business_account',
  business_closed_account_record: 'business_account',
};

const LEGACY_STARTS_WHEN_MAP = {
  on_account_closure: 'account_closed',
  after_inactivity: 'account_closed',
  on_manual_retention_start: 'account_closed',
};

const LEGACY_PERIOD_MAP = {
  '120_days': '120_days',
};

const LEGACY_FINAL_ACTION_MAP = {
  purge_record: 'delete_permanently',
  de_identify_record: 'anonymise',
  keep_metadata_only: 'anonymise',
  not_applicable: '',
};

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? String(value);
}

export function operatorIsMulti(operator) {
  return CONDITION_OPERATOR_OPTIONS.find((option) => option.value === operator)?.multi === true;
}

export function withUnavailableLabel(label, available) {
  if (!label) return available === false ? 'Unavailable' : '';
  return available === false ? `${label} Unavailable` : label;
}

export function normalizeRetentionRecordType(value) {
  if (!value) return '';
  return LEGACY_RECORD_TYPE_MAP[value] || value;
}

export function normalizeStartsWhen(value) {
  if (!value) return '';
  return LEGACY_STARTS_WHEN_MAP[value] || value;
}

export function normalizeRetentionPeriodValue(raw = {}) {
  const period = raw.retentionPeriod || '';
  if (RETENTION_PERIOD_OPTIONS.some((option) => option.value === period)) return period;
  if (LEGACY_PERIOD_MAP[period]) return LEGACY_PERIOD_MAP[period];
  if (period === 'custom_duration') {
    const amount = String(raw.customDurationValue || '').trim();
    const unit = raw.customDurationUnit;
    if (amount === '120' && unit === 'days') return '120_days';
  }
  return period;
}

export function normalizeFinalAction(value) {
  if (!value) return '';
  return LEGACY_FINAL_ACTION_MAP[value] || value;
}

export function getRecordTypeDefinition(value) {
  return RECORD_TYPE_OPTIONS.find((option) => option.value === normalizeRetentionRecordType(value)) || null;
}

export function getStartsWhenDefinition(value) {
  return STARTS_WHEN_OPTIONS.find((option) => option.value === normalizeStartsWhen(value)) || null;
}

export function getRetentionPeriodDefinition(value) {
  return RETENTION_PERIOD_OPTIONS.find((option) => option.value === value) || null;
}

export function getFinalActionDefinition(value) {
  return FINAL_ACTION_OPTIONS.find((option) => option.value === normalizeFinalAction(value)) || null;
}

export function getConditionFieldDefinition(value) {
  return CONDITION_FIELD_OPTIONS.find((option) => option.value === value) || null;
}

export function isRecordTypeAvailable(value) {
  if (!value) return true;
  const definition = getRecordTypeDefinition(value);
  return Boolean(definition && definition.available !== false);
}

export function isStartsWhenAvailable(value) {
  if (!value) return true;
  const definition = getStartsWhenDefinition(value);
  return Boolean(definition && definition.available !== false && definition.defined !== false);
}

export function isRetentionPeriodAvailable(value) {
  if (!value) return true;
  const definition = getRetentionPeriodDefinition(value);
  return Boolean(definition && definition.available !== false && definition.amount && definition.unit && definition.calculation);
}

export function isFinalActionAvailable(value, recordType) {
  if (!value) return true;
  const definition = getFinalActionDefinition(value);
  if (!definition || definition.available === false || definition.defined === false || !definition.scope) return false;
  if (recordType && Array.isArray(definition.recordTypes) && !definition.recordTypes.includes(normalizeRetentionRecordType(recordType))) {
    return false;
  }
  return true;
}

export function getStartsWhenOptions(recordType) {
  if (!recordType) return [];
  const type = normalizeRetentionRecordType(recordType);
  return STARTS_WHEN_OPTIONS.filter((option) => option.recordTypes.includes(type));
}

export function getRetentionPeriodOptions(recordType) {
  if (!recordType) return [];
  const type = normalizeRetentionRecordType(recordType);
  return RETENTION_PERIOD_OPTIONS.filter((option) => (
    option.available !== false
    && (!option.recordTypes?.length || option.recordTypes.includes(type))
  ));
}

export function getFinalActionOptions(recordType) {
  if (!recordType) return [];
  const type = normalizeRetentionRecordType(recordType);
  return FINAL_ACTION_OPTIONS.filter((option) => option.recordTypes.includes(type)).map((option) => ({
    value: option.value,
    label: option.label,
  }));
}

export function getAvailableConditionFields(recordType, startsWhen) {
  if (!recordType || !startsWhen) return [];
  const type = normalizeRetentionRecordType(recordType);
  const start = normalizeStartsWhen(startsWhen);
  return CONDITION_FIELD_OPTIONS.filter((field) => (
    field.available !== false
    && !field.repeatsScope
    && field.recordTypes.includes(type)
    && field.startsWhen.includes(start)
  )).map((field) => ({ value: field.value, label: field.label }));
}

export function getConditionValueOptions(field) {
  return getConditionFieldDefinition(field)?.values || [];
}

export function getRetentionRecordTypeLabel(moduleOrType, recordType) {
  const value = recordType || moduleOrType;
  return withUnavailableLabel(labelFromOptions(RECORD_TYPE_OPTIONS, normalizeRetentionRecordType(value)), isRecordTypeAvailable(value));
}

export function getRecordTypeCoverage(value) {
  return getRecordTypeDefinition(value)?.coverage || '';
}

export function getRecordTypeReference(value) {
  return getRecordTypeDefinition(value)?.reference || '';
}

export function getStartsWhenLabel(value) {
  return withUnavailableLabel(labelFromOptions(STARTS_WHEN_OPTIONS, normalizeStartsWhen(value)), isStartsWhenAvailable(value));
}

export function getStartsWhenMeaning(value) {
  return getStartsWhenDefinition(value)?.meaning || '';
}

export function getStartsWhenDateTimeBasis(value) {
  return getStartsWhenDefinition(value)?.dateTimeBasis || '';
}

export function getRetentionPeriodLabel(value) {
  return withUnavailableLabel(labelFromOptions(RETENTION_PERIOD_OPTIONS, value), isRetentionPeriodAvailable(value));
}

export function getRetentionPeriodCalculation(value) {
  return getRetentionPeriodDefinition(value)?.calculation || '';
}

export function getFinalActionLabel(value, recordType) {
  return withUnavailableLabel(labelFromOptions(FINAL_ACTION_OPTIONS, normalizeFinalAction(value)), isFinalActionAvailable(value, recordType));
}

export function getFinalActionScope(value) {
  return getFinalActionDefinition(value)?.scope || '';
}

export function getConditionFieldLabel(value) {
  const definition = getConditionFieldDefinition(value);
  return withUnavailableLabel(labelFromOptions(CONDITION_FIELD_OPTIONS, value), definition ? definition.available !== false : false);
}

export function getConditionOperatorLabel(value) {
  return labelFromOptions(CONDITION_OPERATOR_OPTIONS, value);
}

export function getConditionValueLabel(field, value) {
  return labelFromOptions(getConditionValueOptions(field), value) || String(value || '');
}

export function labelRetentionPeriod(rule) {
  const period = normalizeRetentionPeriodValue(rule || {});
  return getRetentionPeriodLabel(period) || '—';
}

export function nextRetentionRuleId(rules) {
  const nums = (Array.isArray(rules) ? rules : [])
    .map((rule) => String(rule?.id || '').match(/^RET-(\d+)$/i))
    .filter(Boolean)
    .map((match) => Number(match[1]))
    .filter((num) => Number.isFinite(num));
  const nextNum = (nums.length ? Math.max(...nums) : 0) + 1;
  return `RET-${String(nextNum).padStart(4, '0')}`;
}

export function createEmptyCondition() {
  return { id: `cond-${Date.now()}`, field: '', operator: '', values: [] };
}

export function createEmptyRetentionForm() {
  return {
    id: '',
    ruleName: '',
    recordType: '',
    startsWhen: '',
    retentionPeriod: '',
    finalAction: '',
    conditions: [],
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

function normalizeCondition(raw) {
  const values = Array.isArray(raw?.values)
    ? raw.values.filter(Boolean)
    : raw?.comparisonValue
      ? [raw.comparisonValue]
      : raw?.value
        ? [raw.value]
        : [];
  return {
    id: raw?.id || `cond-${Math.random().toString(36).slice(2, 8)}`,
    field: raw?.field || raw?.conditionField || '',
    operator: raw?.operator || '',
    values,
  };
}

export function applyRetentionSelectionChange(form, field, value) {
  const next = { ...form, [field]: value };
  if (field === 'recordType') {
    const starts = getStartsWhenOptions(next.recordType).map((option) => option.value);
    if (next.startsWhen && !starts.includes(next.startsWhen)) {
      /* keep incompatible start visible for correction */
    }
    const periods = getRetentionPeriodOptions(next.recordType).map((option) => option.value);
    if (next.retentionPeriod && !periods.includes(next.retentionPeriod)) {
      /* keep incompatible period visible for correction */
    }
    const actions = getFinalActionOptions(next.recordType).map((option) => option.value);
    if (next.finalAction && !actions.includes(next.finalAction)) {
      /* keep incompatible action visible for correction */
    }
  }
  return next;
}

export function normalizeRetentionForm(raw = {}) {
  const recordType = normalizeRetentionRecordType(raw.recordType);
  const startsWhen = normalizeStartsWhen(raw.startsWhen || raw.retentionTrigger);
  const retentionPeriod = normalizeRetentionPeriodValue(raw);
  const finalAction = normalizeFinalAction(raw.finalAction);
  return {
    ...createEmptyRetentionForm(),
    ...raw,
    recordType,
    startsWhen,
    retentionPeriod,
    finalAction,
    conditions: Array.isArray(raw.conditions) ? raw.conditions.map(normalizeCondition) : [],
    ruleName: raw.ruleName || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
  };
}

function conditionValues(condition) {
  return [...new Set((condition?.values || []).map((value) => String(value || '').trim()).filter(Boolean))];
}

export function conditionFingerprint(condition) {
  return [
    condition.field || '',
    condition.operator || '',
    conditionValues(condition).slice().sort().join(','),
  ].join('|');
}

export function equivalentConditions(a = [], b = []) {
  const left = (a || []).map(conditionFingerprint).sort().join('||');
  const right = (b || []).map(conditionFingerprint).sort().join('||');
  return left === right;
}

function positiveValuesForField(conditions, field) {
  const positives = [];
  (conditions || []).forEach((condition) => {
    if (condition.field !== field) return;
    const values = conditionValues(condition);
    if (condition.operator === 'equals' || condition.operator === 'is_any_of') positives.push(...values);
  });
  return [...new Set(positives)];
}

export function conditionsMutuallyExclusive(a = [], b = []) {
  if (!(a || []).length || !(b || []).length) return false;
  const fields = [...new Set([...(a || []).map((condition) => condition.field), ...(b || []).map((condition) => condition.field)].filter(Boolean))];
  return fields.some((field) => {
    const left = positiveValuesForField(a, field);
    const right = positiveValuesForField(b, field);
    if (!left.length || !right.length) return false;
    return left.every((value) => !right.includes(value)) && right.every((value) => !left.includes(value));
  });
}

export function retentionCoverageOverlaps(a, b) {
  if (normalizeRetentionRecordType(a.recordType) !== normalizeRetentionRecordType(b.recordType)) return false;
  if (conditionsMutuallyExclusive(a.conditions, b.conditions)) return false;
  return true;
}

export function findDuplicateRetentionRule(rules, candidate, excludeId) {
  const form = normalizeRetentionForm(candidate);
  return (rules || []).map(normalizeRetentionForm).find((rule) => (
    rule.id !== excludeId
    && rule.recordType === form.recordType
    && rule.startsWhen === form.startsWhen
    && rule.retentionPeriod === form.retentionPeriod
    && rule.finalAction === form.finalAction
    && equivalentConditions(rule.conditions, form.conditions)
  )) || null;
}

export function findConflictingRetentionRule(rules, candidate, excludeId) {
  const form = normalizeRetentionForm(candidate);
  return (rules || []).map(normalizeRetentionForm).find((rule) => {
    if (rule.id === excludeId || !retentionCoverageOverlaps(rule, form)) return false;
    return (
      rule.startsWhen !== form.startsWhen
      || rule.retentionPeriod !== form.retentionPeriod
      || rule.finalAction !== form.finalAction
    );
  }) || null;
}

export function findOverlappingRetentionRule(rules, candidate, excludeId) {
  const form = normalizeRetentionForm(candidate);
  const duplicate = findDuplicateRetentionRule(rules, form, excludeId);
  if (duplicate) return null;
  const conflict = findConflictingRetentionRule(rules, form, excludeId);
  if (conflict) return null;
  return (rules || []).map(normalizeRetentionForm).find((rule) => (
    rule.id !== excludeId && retentionCoverageOverlaps(rule, form)
  )) || null;
}

export function findDifferentStartOverlap(rules, candidate, excludeId) {
  const form = normalizeRetentionForm(candidate);
  return (rules || []).map(normalizeRetentionForm).find((rule) => (
    rule.id !== excludeId
    && retentionCoverageOverlaps(rule, form)
    && rule.startsWhen !== form.startsWhen
  )) || null;
}

export function formatConditionLine(condition) {
  const field = getConditionFieldLabel(condition.field);
  const operator = getConditionOperatorLabel(condition.operator);
  const values = (condition.values || []).map((value) => getConditionValueLabel(condition.field, value));
  return [field, operator, values.join(', ')].filter(Boolean).join(' · ');
}

export function formatConditionsPreview(conditions = []) {
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map(formatConditionLine);
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return {
    text: `${lines[0]} · +${lines.length - 1} more`,
    extraCount: lines.length - 1,
    lines,
  };
}

export function formatRetentionDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${suffix}`;
}

export function sortRetentionRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    return bTime - aTime;
  });
}

function actorFields(stamp, name) {
  return {
    addedBy: name,
    lastUpdatedBy: name,
    dateAdded: stamp,
    lastUpdated: stamp,
  };
}

/** Table 1 illustrative rows — confirmed ordinary-account policy display only. */
export function buildSampleRetentionRules() {
  return [
    {
      id: 'RET-0002',
      ruleName: 'Business Account Closure',
      recordType: 'business_account',
      startsWhen: 'account_closed',
      retentionPeriod: '120_days',
      finalAction: 'anonymise',
      conditions: [],
      ...actorFields('2026-08-15T09:35:00+10:00', 'Priya Shah'),
    },
    {
      id: 'RET-0001',
      ruleName: 'Customer Account Closure',
      recordType: 'customer_account',
      startsWhen: 'account_closed',
      retentionPeriod: '120_days',
      finalAction: 'anonymise',
      conditions: [],
      ...actorFields('2026-08-15T09:20:00+10:00', 'Alex Morgan'),
    },
  ];
}

/** @deprecated aliases — unit calculation text retained for documentation callers */
export const RETENTION_UNIT_CALCULATIONS = {
  days: DAYS_CALCULATION,
  months: MONTHS_CALCULATION,
  years: YEARS_CALCULATION,
};

export const RETENTION_MODULE_OPTIONS = [];
export const RECORD_TYPES_BY_RETENTION_MODULE = {};
export const RETENTION_CONDITION_OPTIONS = [];
export const RETENTION_TRIGGER_OPTIONS = STARTS_WHEN_OPTIONS.map(({ value, label }) => ({ value, label }));
export const RETENTION_TREATMENT_OPTIONS = [];
export const LINKED_RECORD_TREATMENT_OPTIONS = [];
export const FINAL_REVIEW_OPTIONS = [];
export const DURATION_UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];
export const REVIEWER_RESOLUTION_OPTIONS = [];
export const PARENT_REFERENCE_PRESERVATION_OPTIONS = [];
export const RETENTION_STATUS_OPTIONS = [];
export const DURING_RETENTION_RULE_OPTIONS = [];
export const HOLD_EFFECT_OPTIONS = [];

export function normalizeRetentionModule(value) {
  return value || '';
}

export function normalizeRetentionRuleScope(module, recordType) {
  return { module: module || '', recordType: normalizeRetentionRecordType(recordType) };
}

export function normalizeRetentionRuleStatus() {
  return '';
}

export function recordTypesForRetentionModule() {
  return RECORD_TYPE_OPTIONS.map(({ value, label }) => ({ value, label }));
}

export function getRetentionModuleLabel() {
  return '';
}

export function getRetentionTriggerLabel(value) {
  return getStartsWhenLabel(value);
}

export function getRetentionTreatmentLabel() {
  return '';
}

export function getLinkedRecordTreatmentLabel() {
  return '';
}

export function getFinalReviewLabel() {
  return '';
}

export function getFinalActionOptionsForPeriod() {
  return FINAL_ACTION_OPTIONS.filter((option) => option.recordTypes.length).map(({ value, label }) => ({ value, label }));
}

export function retentionRuleDuplicateKey(rule) {
  const form = normalizeRetentionForm(rule);
  return [form.recordType, form.startsWhen, form.retentionPeriod, form.finalAction, (form.conditions || []).map(conditionFingerprint).sort().join('||')].join('|');
}

export function retentionRuleConflictKey(rule) {
  const form = normalizeRetentionForm(rule);
  return form.recordType;
}

export function findDuplicateActiveRetentionRule(rules, candidate, excludeId) {
  return findConflictingRetentionRule(rules, candidate, excludeId);
}

export function requiresApprovalConfigurationReason() {
  return false;
}
